import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "OilFuelFiltrationApplications",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Oil, Fuel & Petrochemical Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Filter Mesh & Elements for Oil and Fuel Systems",
      lead: "Precision metallic filter media, diffusion-bonded sintered laminates, and cylindrical elements engineered for high-pressure fuel lines, hydraulic power loops, turbine lube circuits, and heavy fuel oil conditioning with zero fiber migration.",
      secondaryCta: {
        label: "Oil & Fuel Filtration",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
        alt: "Stainless steel fuel and hydraulic fluid filtration mesh element detail",
      },
      cardNote: "All-metal construction & zero downstream fiber migration",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rating Range", value: "1 µm – 250 µm" },
        { label: "Standard Weaves", value: "Plain / Twill Dutch / Sintered" },
        { label: "Standard Alloys", value: "AISI 304L / 316L / Inconel" },
        { label: "Cleanability", value: "Ultrasonic / Reverse Flush" },
      ],
    },
    overview: {
      eyebrow: "Fluid Power & Fuel Conditioning",
      heading: "Reliable Particulate Retention for High-Pressure Hydrocarbon & Hydraulic Streams",
      paragraphs: [
        "Modern hydraulic power transmission loops, aviation kerosene delivery networks, and turbine lubrication circuits operate under severe mechanical pulsations and sustained differential pressures. Conventional disposable polymer or cellulose media carry significant risks of media channeling, structural collapse, and downstream fiber shedding that can damage precision servo valves, bearings, and injection nozzles.",
        "MutualFilter supplies rigid, all-metal filtration media designed to resolve these operational vulnerabilities. Incorporating precision plain Dutch weave wire cloth, high-density twilled Dutch cloth, and diffusion-bonded multilayer sintered mesh laminates, our elements sustain calibrated pore geometry and precise particle cut-off thresholds even during rapid fluid flow transients.",
        "Constructed from corrosion-resistant AISI 304L and 316L stainless steel as well as high-performance nickel alloys, every element provides broad compatibility with synthetic lubricants, aromatic fuels, and heavy fuel oils (HFO). The robust all-welded metallic structure supports repeated cleaning through solvent backflushing and ultrasonic agitation.",
      ],
      keyPoints: [
        "Diffusion-bonded sintered layers and dense Dutch weaves maintain pore geometry under severe differential pressure pulsations",
        "All-welded metallic architecture eliminates downstream fiber migration and protects sensitive servo valves and fuel injectors",
        "Fully regenerable via ultrasonic cleaning and reverse-flush cycles, reducing disposable consumable waste in continuous service",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
        alt: "Stainless steel fuel and hydraulic fluid filtration mesh element detail",
      },
    },
    specTable: {
      heading: "Filter Media Configurations for Oil and Fuel Service",
      intro: "Representative metallic filter media configurations tailored for hydraulic systems, aviation fuel delivery, lube oil circuits, and heavy fuel conditioning.",
      columns: [
        { key: "service", label: "Application Circuit", align: "left" },
        { key: "media", label: "Media Construction", align: "left" },
        { key: "rating", label: "Nominal Rating", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "cleaning", label: "Cleaning Method", align: "right" },
      ],
      rows: [
        {
          service: "High-Pressure Hydraulic Power",
          media: "Pleated multilayer sintered mesh",
          rating: "3 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Ultrasonic bath / backflush",
        },
        {
          service: "Aviation Fuel & Kerosene Delivery",
          media: "Twilled Dutch weave wire cloth",
          rating: "1 µm – 15 µm",
          alloys: "AISI 304L / 316L",
          cleaning: "Solvent rinse / reverse flush",
        },
        {
          service: "Turbine Lubrication & Bearing Oil",
          media: "Plain Dutch weave cylindrical cartridge",
          rating: "10 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Ultrasonic / solvent wash",
        },
        {
          service: "Heavy Fuel Oil (HFO) Conditioning",
          media: "Welded wire mesh basket / cylinder",
          rating: "50 µm – 250 µm",
          alloys: "AISI 316L / Inconel",
          cleaning: "Steam purge / chemical wash",
        },
      ],
      caption: "All aperture ratings, cartridge dimensions, and end fitting configurations are engineered to order per circuit operating specifications.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our all-metal fuel and hydraulic filtration solutions provide mechanical endurance, dependable micron ratings, and repeatable cleanability.",
      items: [
        {
          title: "Rigid Collapse Resistance",
          text: "Heavy-gauge support meshes and inner perforated cores protect fine filtration layers from mechanical collapse during cold starts and high-viscosity pressure surges.",
        },
        {
          title: "Zero Fiber Shedding",
          text: "Precision wire cloth and metallurgical diffusion bonding eliminate media migration, ensuring downstream fluid streams remain entirely free of particulate contamination.",
        },
        {
          title: "Broad Fluid Compatibility",
          text: "Passivated AISI 304L, 316L, and high-nickel alloys resist oxidation, sulfur compounds, acidic hydrocarbons, and aggressive synthetic hydraulic fluids.",
        },
        {
          title: "Repeatable Cleanability",
          text: "Durable welded seam construction enables complete ultrasonic regeneration and reverse fluid purging, delivering multi-cycle service life without media degradation.",
        },
      ],
    },
    related: {
      heading: "Related Oil & Fuel Applications and Products",
      intro: "Explore complementary fluid separation processes and precision metallic filter products engineered by MutualFilter.",
      cards: [
        {
          title: "Oil, Fuel & Petrochemical Filtration",
          text: "Comprehensive metallic filtration solutions for upstream separation, refinery streams, and high-pressure fluid power.",
          to: "/OilFuelFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Oil, fuel and petrochemical filtration overview showing stainless steel filter media",
          },
        },
        {
          title: "Oil and Gas Separation",
          text: "Downhole sand control, gas scrubber mist elimination, and produced water separation assemblies built for severe hydrocarbon service.",
          to: "/OilGasSeparationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Oil & Gas Separation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Stainless steel multi-layer mesh screen for oil and gas solid-liquid separation",
          },
        },
        {
          title: "Chemical & Process Filtration",
          text: "Catalyst bed retention screens, reforming compressor protection, and high-temperature hydrocarbon filtration elements.",
          to: "/ChemicalFiltrationPage#petrochemical",
          badge: "Sibling Application",
          linkLabel: "Explore Chemical & Process",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
            alt: "Industrial high-pressure filtration vessel in a petrochemical processing facility",
          },
        },
        {
          title: "Filter Components",
          text: "Cylindrical cartridges, pleated elements, and custom welded filter assemblies for high-pressure fuel lines and lube circuits.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Stainless steel pleated and cylindrical filter cartridges for oil and fuel service",
          },
        },
        {
          title: "Sintered Wire Mesh",
          text: "Diffusion-bonded multilayer laminate sheets and discs providing rigid pore architecture and non-shedding particulate retention.",
          to: "/SinteredWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Sintered Wire Mesh",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Multilayer sintered wire mesh laminate disc for hydraulic and oil filtration",
          },
        },
      ],
    },
    cta: {
      id: "oil-fuel-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Oil or Fuel Filtration Specification",
      lead: "Provide your required micron rating, flow rate, fluid type, and envelope dimensions for an engineering review and quotation.",
      secondaryLabel: "Oil & Fuel Filtration",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "OilFuelFiltrationApplications",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "油品、燃料与石化过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "燃油、液压油与润滑介质过滤精密金属网及滤芯",
      lead: "专为高压燃油输配管线、液压动力控制回路、汽轮机与压缩机润滑系统以及重油净化打造的高强度不锈钢精密金属网与多层烧结元件，确保稳定的微米级截留与零纤维脱落。",
      secondaryCta: {
        label: "油品与燃料过滤总览",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
        alt: "不锈钢燃油与液压流体过滤精密滤芯微观结构",
      },
      cardNote: "全金属刚性结构与下游零纤维脱落保障",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "过滤精度范围", value: "1 µm – 250 µm" },
        { label: "常用编织形式", value: "平纹 / 斜纹荷兰 / 多层烧结" },
        { label: "常用材质牌号", value: "AISI 304L / 316L / Inconel" },
        { label: "清洗再生方式", value: "超声波清洗 / 反向冲洗" },
      ],
    },
    overview: {
      eyebrow: "流体动力与燃料净化",
      heading: "面向高压液压与碳氢化合物介质的稳定微细颗粒截留",
      paragraphs: [
        "现代液压动力系统、航空煤油加注网络以及透平润滑油回路常年处于严苛的高压流体脉动与持续压差工况。常规聚合物或纤维素一次性滤材存在介质沟流、受压塌陷以及纤维介质脱落的风险，极易造成高精度伺服阀、轴瓦及喷油嘴磨损损坏。",
        "MutualFilter 提供工程级全金属过滤介质以消除此类失效隐患。我们采用精密平纹荷兰编织丝网、高密度斜纹荷兰密纹布以及扩散焊接多层烧结网板，在瞬态强脉动与冷启动高粘度冲击下仍能维持高度稳定的孔径截面与截留界限。",
        "滤芯元件选用耐腐蚀 AISI 304L、316L 不锈钢及特种镍基合金精工焊接而成，与合成液压油、航空燃料及重油（HFO）具备广泛的化学相容性。全金属熔焊结构支持反复溶剂反洗与超声波清洗再生，显著延长元件服役周期。",
      ],
      keyPoints: [
        "扩散焊接烧结网与致密荷兰编织结构在极端脉动压差下维持孔径稳定，防止介质变形失稳",
        "全金属熔焊结构消除下游纤维与颗粒脱落风险，有效保护精密伺服阀与喷油嘴",
        "耐受超声波深度清洗与反冲洗多次循环再生，大幅降低长期运行中的滤芯耗材消耗",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
        alt: "不锈钢燃油与液压流体过滤精密滤芯结构细节",
      },
    },
    specTable: {
      heading: "燃油与液压油品过滤典型介质配置",
      intro: "以下列出适用于高压液压回路、航空煤油净化、透平润滑油以及重油预处理工况的典型金属过滤介质配置。",
      columns: [
        { key: "service", label: "应用回路 / 工况", align: "left" },
        { key: "media", label: "滤材结构形式", align: "left" },
        { key: "rating", label: "名义过滤精度", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "cleaning", label: "清洗再生方式", align: "right" },
      ],
      rows: [
        {
          service: "高压液压动力传输系统",
          media: "折叠式多层烧结金属网滤芯",
          rating: "3 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "超声波清洗槽 / 反冲洗",
        },
        {
          service: "航空煤油与燃油净化管线",
          media: "斜纹荷兰密纹编织金属布",
          rating: "1 µm – 15 µm",
          alloys: "AISI 304L / 316L",
          cleaning: "溶剂漂洗 / 反向流体冲洗",
        },
        {
          service: "汽轮机润滑油与压缩机轴承回路",
          media: "平纹荷兰编织圆柱形滤筒",
          rating: "10 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "超声波 / 溶剂循环冲洗",
        },
        {
          service: "重油 (HFO) 净化与燃料预处理",
          media: "焊接不锈钢编织网滤篮 / 圆筒",
          rating: "50 µm – 250 µm",
          alloys: "AISI 316L / Inconel",
          cleaning: "蒸汽吹扫 / 化学浸洗",
        },
      ],
      caption: "各项过滤精度、滤芯结构尺寸与端盖接口均可根据客户液压系统参数与安装要求定制设计。",
    },
    features: {
      heading: "关键运行与结构制造优势",
      intro: "我们的全金属燃油与液压过滤方案兼具结构抗压刚性、高精度截留界限以及优异的反复再生性能。",
      items: [
        {
          title: "卓越的抗压差塌陷刚性",
          text: "厚规格金属支撑网与高强度冲孔内骨架协同工作，有效抵御系统冷启动高粘度冲击与脉动负荷引起的结构失稳。",
        },
        {
          title: "零介质脱落与高清洁度",
          text: "精密金属丝布与冶金级扩散焊接烧结彻底杜绝纤维脱落，确保下游动力回路流体达到严苛的清洁度指标。",
        },
        {
          title: "广泛的介质化学相容性",
          text: "酸洗钝化 AISI 304L、316L 及镍基耐蚀合金抵抗硫化物腐蚀、酸性碳氢化合物及合成润滑液的长期侵蚀。",
        },
        {
          title: "可靠的反复清洗再生能力",
          text: "全焊缝坚固构造支持反复超声波脱附清洗与流体反冲再生，实现多次循环使用并减少危险废弃物产生。",
        },
      ],
    },
    related: {
      heading: "相关油品过滤应用与推荐产品",
      intro: "浏览 MutualFilter 为流体动力、石油化工及高端能源装备制造的互补金属过滤方案与精密产品。",
      cards: [
        {
          title: "油品、燃料与石化过滤",
          text: "涵盖油气井口分离、炼化工艺流体净化与高压液压动力的全金属过滤材料综合方案。",
          to: "/OilFuelFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "油品燃料石化过滤总览及不锈钢过滤网元件",
          },
        },
        {
          title: "油气分离与防砂净化",
          text: "针对井口防砂、天然气除沫及采出水处理设计的重载丝网、烧结板与楔形丝分离构件。",
          to: "/OilGasSeparationPage",
          badge: "同级应用",
          linkLabel: "探索油气分离",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "油气固液分离多层不锈钢丝网滤板",
          },
        },
        {
          title: "化工与工艺过滤",
          text: "催化裂化床层保护、重整压缩机气体除杂及高温强腐蚀工艺流体截留元件。",
          to: "/ChemicalFiltrationPage#petrochemical",
          badge: "同级应用",
          linkLabel: "探索化工与工艺过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
            alt: "石化设施高压过滤容器与工业滤网元件",
          },
        },
        {
          title: "过滤组件与滤芯",
          text: "全焊接不锈钢圆柱及折叠滤芯元件，专用于高压燃油管路、液压系统与润滑油过滤。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "油品与燃油用不锈钢折叠及圆柱滤芯",
          },
        },
        {
          title: "多层金属烧结网",
          text: "扩散焊接金属烧结复合板，提供高刚性孔隙结构、高抗压差强度与无介质脱落保证。",
          to: "/SinteredWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索烧结金属网",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "液压与油品过滤用多层烧结金属网滤盘",
          },
        },
      ],
    },
    cta: {
      id: "oil-fuel-quote",
      eyebrow: "定制方案咨询",
      heading: "获取燃油与液压过滤滤芯及筛网技术报价",
      lead: "请提供您的目标过滤精度、工作流量、介质类型及滤芯接口尺寸，我们的工程技术人员将为您提供详细的技术选型与报价方案。",
      secondaryLabel: "返回油品与燃料过滤",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "材质检验报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "OilFuelFiltrationApplications",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Filtración de Aceites, Combustibles y Petroquímica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas y Elementos de Acero Inoxidable para Combustible y Fluidos Hidráulicos",
      lead: "Medios filtrantes metálicos de precisión, laminados sinterizados unidos por difusión y elementos cilíndricos diseñados para circuitos hidráulicos de alta presión, queroseno de aviación, lubricación y acondicionamiento de fueloil pesado sin desprendimiento de fibras.",
      secondaryCta: {
        label: "Filtración de Aceite y Combustible",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
        alt: "Detalle de elemento filtrante de malla de acero inoxidable para combustible y fluidos hidráulicos",
      },
      cardNote: "Construcción totalmente metálica y cero migración de fibras",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Filtración", value: "1 µm – 250 µm" },
        { label: "Tejidos Estándar", value: "Plain / Twill Dutch / Sinterizado" },
        { label: "Aleaciones Típicas", value: "AISI 304L / 316L / Inconel" },
        { label: "Limpieza", value: "Ultrasonidos / Retrolavado" },
      ],
    },
    overview: {
      eyebrow: "Potencia Fluida y Acondicionamiento de Combustible",
      heading: "Retención Precisa de Partículas para Hidrocarburos y Fluidos Hidráulicos a Alta Presión",
      paragraphs: [
        "Los circuitos hidráulicos de potencia, las redes de distribución de queroseno de aviación y los sistemas de lubricación de turbinas operan bajo fuertes pulsaciones y presiones diferenciales continuas. Los medios convencionales desechables de polímero o celulosa presentan riesgos de canalización, colapso estructural y desprendimiento de fibras que dañan servoválvulas, cojinetes e inyectores.",
        "MutualFilter suministra medios filtrantes totalmente metálicos diseñados para eliminar estas vulnerabilidades. Mediante telas de tejido Dutch liso, mallas Dutch cruzadas de alta densidad y laminados multicapa sinterizados por difusión, nuestros elementos mantienen una geometría de poro inalterable y puntos de corte exactos incluso ante fluctuaciones bruscas de flujo.",
        "Fabricados en aceros inoxidables AISI 304L y 316L así como en aleaciones de níquel, estos filtros garantizan una amplia compatibilidad con aceites sintéticos, combustibles aromáticos y fueloil pesado (HFO). Su estructura soldada permite múltiples ciclos de regeneración mediante retrolavado y limpieza por ultrasonidos.",
      ],
      keyPoints: [
        "Laminados sinterizados por difusión y tejidos Dutch densos que preservan los poros bajo pulsaciones de presión severas",
        "Estructura totalmente metálica que elimina la migración de fibras y protege servoválvulas e inyectores de combustible",
        "Regenerable mediante ultrasonidos y retrolavado continuo, reduciendo drásticamente los consumibles desechables",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
        alt: "Detalle del elemento filtrante de malla de acero inoxidable para combustible y aceite",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios Filtrantes para Aceites y Combustibles",
      intro: "Medios metálicos representativos para circuitos hidráulicos de potencia, aviación, lubricación y acondicionamiento de combustibles pesados.",
      columns: [
        { key: "service", label: "Circuito / Aplicación", align: "left" },
        { key: "media", label: "Tipo de Medio Filtrante", align: "left" },
        { key: "rating", label: "Grado Nominal", align: "right" },
        { key: "alloys", label: "Aleaciones Típicas", align: "right" },
        { key: "cleaning", label: "Método de Limpieza", align: "right" },
      ],
      rows: [
        {
          service: "Potencia Hidráulica de Alta Presión",
          media: "Malla sinterizada multicapa plisada",
          rating: "3 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Baño de ultrasonidos / retrolavado",
        },
        {
          service: "Queroseno de Aviación y Combustible",
          media: "Tela metálica de tejido Twill Dutch",
          rating: "1 µm – 15 µm",
          alloys: "AISI 304L / 316L",
          cleaning: "Lavado con solvente / retrolavado",
        },
        {
          service: "Lubricación de Turbinas y Cojinetes",
          media: "Cartucho cilíndrico de tejido Dutch liso",
          rating: "10 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Ultrasonidos / recirculación solvente",
        },
        {
          service: "Acondicionamiento de Fueloil Pesado (HFO)",
          media: "Cesta o cilindro de malla soldada",
          rating: "50 µm – 250 µm",
          alloys: "AISI 316L / Inconel",
          cleaning: "Purga de vapor / lavado químico",
        },
      ],
      caption: "Todas las especificaciones micrométricas, dimensiones de cartuchos y conexiones finales se fabrican a medida según los requerimientos del circuito.",
    },
    features: {
      heading: "Ventajas Operativas y Constructivas",
      intro: "Nuestras soluciones totalmente metálicas ofrecen alta resistencia mecánica, retención micrométrica confiable y excelente limpiabilidad.",
      items: [
        {
          title: "Resistencia Rígida al Colapso",
          text: "Mallas de soporte pesadas y tubos perforados interiores evitan el colapso durante arranques en frío con fluidos de alta viscosidad.",
        },
        {
          title: "Cero Desprendimiento de Fibras",
          text: "Telas de precisión y sinterización por difusión garantizan que los fluidos aguas abajo se mantengan libres de fibras y desprendimientos.",
        },
        {
          title: "Amplia Compatibilidad Química",
          text: "Aceros AISI 304L, 316L y aleaciones de níquel resisten hidrocarburos ácidos, compuestos de azufre y aceites hidráulicos sintéticos.",
        },
        {
          title: "Limpiabilidad Repetible",
          text: "Construcción soldada duradera que permite regeneración reiterada por ultrasonidos y retrolavado continuo sin degradación del medio.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Productos Relacionados",
      intro: "Explore soluciones complementarias en filtración de hidrocarburos y medios metálicos de precisión diseñados por MutualFilter.",
      cards: [
        {
          title: "Filtración de Aceites, Combustibles y Petroquímica",
          text: "Soluciones completas de filtración metálica para procesos de refinería, separación y circuitos de alta presión.",
          to: "/OilFuelFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Visión general de filtración de aceites y combustibles",
          },
        },
        {
          title: "Separación de Petróleo y Gas",
          text: "Control de arena en pozo, desempañado en depuradores de gas y tratamiento de agua de producción en hidrocarburos.",
          to: "/OilGasSeparationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Petróleo y Gas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Panel filtrante multicapa de acero inoxidable para separación de petróleo y gas",
          },
        },
        {
          title: "Filtración química y de procesos",
          text: "Protección de lechos catalíticos, compresores de reformado y filtración de hidrocarburos a altas temperaturas.",
          to: "/ChemicalFiltrationPage#petrochemical",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Química y Procesos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
            alt: "Recipiente de filtración industrial en planta petroquímica",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cartuchos cilíndricos y elementos plisados totalmente soldados en acero inoxidable para líneas de combustible e hidráulica.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Cartuchos filtrantes de acero inoxidable para combustible y aceite",
          },
        },
        {
          title: "Malla de Alambre Sinterizada",
          text: "Laminados sinterizados por difusión que ofrecen rigidez de poro y retención de partículas sin desprendimiento.",
          to: "/SinteredWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Malla Sinterizada",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado de malla sinterizada para filtración hidráulica",
          },
        },
      ],
    },
    cta: {
      id: "oil-fuel-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Mallas y Elementos de Combustible y Aceite",
      lead: "Indique el grado de filtración requerido, caudal de trabajo, tipo de fluido y dimensiones para una revisión técnica y cotización.",
      secondaryLabel: "Volver a Filtración de Combustibles",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "OilFuelFiltrationApplications",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Öl-, Kraftstoff- und Petrochemiefiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahlsiebe & Filterelemente für Kraftstoff- und Hydrauliksysteme",
      lead: "Präzisions-Metallfiltermedien, diffusionsgeschweißte Sinterlaminate und zylindrische Filterkerzen für Hochdruck-Kraftstoffleitungen, Hydraulikkreisläufe, Turbinenschmierung und Schwerölaufbereitung ohne Fasermigration.",
      secondaryCta: {
        label: "Öl- & Kraftstofffiltration",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
        alt: "Detail eines Edelstahl-Filterelements für Kraftstoff- und Hydraulikfiltration",
      },
      cardNote: "Ganzmetall-Konstruktion & keine Fasermigration im Reinlauf",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 µm – 250 µm" },
        { label: "Gewebetypen", value: "Glatte / Köpertresse / Sinterverbund" },
        { label: "Standardlegierungen", value: "AISI 304L / 316L / Inconel" },
        { label: "Reinigbarkeit", value: "Ultraschall / Rückspülung" },
      ],
    },
    overview: {
      eyebrow: "Fluidtechnik & Kraftstoffaufbereitung",
      heading: "Zuverlässige Partikelabscheidung für Hochdruck-Hydraulik und Kohlenwasserstoffe",
      paragraphs: [
        "Hydraulische Antriebssysteme, Flugkraftstoffnetze und Turbinenschmierölkreisläufe unterliegen starken Druckpulsationen und kontinuierlichen Differenzdrücken. Konventionelle Einwegmedien aus Polymeren oder Zellulose bergen Risiken von Kanalbildung, Kollaps und Fasermigration, die Servoventile, Lager und Einspritzdüsen beschädigen können.",
        "MutualFilter liefert formstabile Ganzmetall-Filtermedien, um diese Schwachstellen zu eliminieren. Durch den Einsatz von glatten Tressengeweben, hochdichten Köpertressen und diffusionsgeschweißten mehrlagigen Sinterlaminaten bleiben Porenquerschnitte und Trenngrenzen auch bei abrupten Strömungsspitzen unverändert.",
        "Gefertigt aus korrosionsbeständigen Edelstählen AISI 304L und 316L sowie Nickelbasislegierungen bieten unsere Filterelemente breite Verträglichkeit mit Synthetikölen, Flugkraftstoffen und Schwerölen (HFO). Die vollverschweißte Metallkonstruktion gestattet wiederholte Reinigungen durch Ultraschall und Gegenstromspülung.",
      ],
      keyPoints: [
        "Diffusionsgebundene Sinterlagen und dichte Tressen bewahren die Porenstruktur unter extremen Differenzdruckstößen",
        "Vollverschweißte Ganzmetallarchitektur schließt Fasermigration aus und schützt Servoventile und Einspritzdüsen",
        "Vollständig regenerierbar durch Ultraschallbäder und Rückspülung, was den Bedarf an Einwegfiltern nachhaltig senkt",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
        alt: "Detail eines Edelstahl-Filterelements für Kraftstoff- und Ölfiltration",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für Öl- und Kraftstoffanwendungen",
      intro: "Repräsentative metallische Filtermedien für Hochdruck-Hydraulik, Kerosinreinigung, Schmierstoffkreisläufe und Schwerölkonditionierung.",
      columns: [
        { key: "service", label: "Einsatzbereich / Kreislauf", align: "left" },
        { key: "media", label: "Filteraufbau", align: "left" },
        { key: "rating", label: "Nennfeinheit", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "cleaning", label: "Reinigungsmethode", align: "right" },
      ],
      rows: [
        {
          service: "Hochdruck-Hydraulikantriebe",
          media: "Plissiertes mehrlagiges Sintergewebe",
          rating: "3 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Ultraschallbad / Rückspülung",
        },
        {
          service: "Flugkraftstoff- & Kerosinleitungen",
          media: "Köpertresse Edelstahldrahtgewebe",
          rating: "1 µm – 15 µm",
          alloys: "AISI 304L / 316L",
          cleaning: "Lösemittelspülung / Rückspülung",
        },
        {
          service: "Turbinenschmierung & Lagerkreisläufe",
          media: "Zylindrische Filterkerze mit glatter Tresse",
          rating: "10 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Ultraschall / Lösemittelspülung",
        },
        {
          service: "Schwerölaufbereitung (HFO)",
          media: "Verschweißter Maschenkorb / Zylinder",
          rating: "50 µm – 250 µm",
          alloys: "AISI 316L / Inconel",
          cleaning: "Dampfblasen / chemische Reinigung",
        },
      ],
      caption: "Alle Nennfeinheiten, Kerzenabmessungen und Endkappenanschlüsse werden auftragsbezogen gefertigt.",
    },
    features: {
      heading: "Betriebliche & Konstruktive Vorteile",
      intro: "Unsere Ganzmetall-Filterlösungen für Kraftstoffe und Öle bieten mechanische Standfestigkeit, präzise Mikron-Trennung und lange Standzeiten.",
      items: [
        {
          title: "Hohe Kollapsfestigkeit",
          text: "Starke Stützgewebe und Lochblechrohre widerstehen Kaltstartbelastungen und Druckspitzen hochviskoser Fluide ohne Verformung.",
        },
        {
          title: "Keine Fasermigration",
          text: "Präzisionsgewebe und diffusionsgebundene Sinterverbunde schließen das Auswaschen von Partikeln und Fasern in den Reinlauf aus.",
        },
        {
          title: "Breite Fluidbeständigkeit",
          text: "Passivierte Edelstähle AISI 304L, 316L und Nickellegierungen widerstehen sauren Kohlenwasserstoffen und synthetischen Hydraulikölen.",
        },
        {
          title: "Wiederholbare Reinigbarkeit",
          text: "Stabile Schweißnähte ermöglichen zuverlässige Ultraschallreinigung und Rückspülung ohne mechanische Schädigung des Filtergefüges.",
        },
      ],
    },
    related: {
      heading: "Verwandte Anwendungen & Produkte",
      intro: "Entdecken Sie weitere Filtrationslösungen für flüssige Kohlenwasserstoffe und industrielle Drahtgewebeprodukte von MutualFilter.",
      cards: [
        {
          title: "Öl-, Kraftstoff- und Petrochemiefiltration",
          text: "Ganzheitliche metallische Trennlösungen für Raffinerieprozesse, Trennanlagen und Hochdruck-Hydraulik.",
          to: "/OilFuelFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Übersicht Öl- und Kraftstofffiltration",
          },
        },
        {
          title: "Öl- und Gastrennung",
          text: "Sandschutz in Förderbohrungen, Tropfenabscheidung in Gaswäschern und Lagerstättenwasseraufbereitung.",
          to: "/OilGasSeparationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Öl- und Gastrennung ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Mehrlagige Edelstahl-Filterplatte für die Öl- und Gasphasentrennung",
          },
        },
        {
          title: "Chemie- und Prozessfiltration",
          text: "Katalysatorbettsiebe, Kompressorschutz und Filterelemente für heiße und aggressive Prozessmedien.",
          to: "/ChemicalFiltrationPage#petrochemical",
          badge: "Geschwisteranwendung",
          linkLabel: "Chemie & Prozess ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
            alt: "Industrieller Hochdruck-Filterbehälter in einer Raffinerie",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Plissierte und zylindrische Edelstahl-Filterkerzen für Hochdruck-Kraftstoffleitungen und Hydrauliksysteme.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Filterkomponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Plissierte und zylindrische Edelstahl-Filterkerzen für Kraftstoffe",
          },
        },
        {
          title: "Gesintertes Drahtgewebe",
          text: "Diffusionsgebundene Sinterlaminate mit starrer Porenarchitektur für zuverlässige Partikelrückhaltung.",
          to: "/SinteredWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Sintergewebe entdecken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Mehrlagige Sinterscheibe für Hydraulik- und Ölfiltration",
          },
        },
      ],
    },
    cta: {
      id: "oil-fuel-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für Kraftstoff- & Hydraulikfilterelemente anfordern",
      lead: "Nennen Sie Feinheit, Durchflussrate, Fluidart und Einbaumaße für eine detaillierte technische Prüfung.",
      secondaryLabel: "Zurück zu Öl- & Kraftstofffiltration",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "OilFuelFiltrationApplications",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Filtration des Huiles, Carburants et Pétrochimie",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toiles et Éléments Filtrants en Inox pour Circuits Carburant et Hydraulique",
      lead: "Médias filtrants métalliques de précision, stratifiés frittés soudés par diffusion et cartouches cylindriques conçus pour les lignes de carburant haute pression, les circuits hydrauliques, la lubrification de turbines et le fioul lourd sans relargage de fibres.",
      secondaryCta: {
        label: "Filtration des Huiles et Carburants",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
        alt: "Détail d'un élément filtrant en toile métallique inox pour carburant et fluide hydraulique",
      },
      cardNote: "Construction tout métal & zéro migration de fibres en aval",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Seuil de Filtration", value: "1 µm – 250 µm" },
        { label: "Tissages Standards", value: "Uni / Sergé Hollandais / Fritté" },
        { label: "Alliages Standards", value: "AISI 304L / 316L / Inconel" },
        { label: "Nettoyabilité", value: "Ultrasons / Rétrolavage" },
      ],
    },
    overview: {
      eyebrow: "Puissance Fluide et Conditionnement Carburant",
      heading: "Rétention Particulaire Précise pour Hydrocarbures et Circuits Hydrauliques Haute Pression",
      paragraphs: [
        "Les circuits de puissance hydraulique, les réseaux d'avitaillement en kérosène et les boucles de lubrification de turbines sont soumis à de fortes pulsations et à des pressions différentielles soutenues. Les médias jetables en polymère ou cellulose présentent des risques majeurs de colmatage par renardage, d'écrasement mécanique et de relargage de fibres endommageant servovalves, paliers et injecteurs.",
        "MutualFilter conçoit des médias filtrants tout métal éliminant ces risques opérationnels. Grâce à des toiles néerlandaises unies, des tissages sergés haute densité et des stratifiés multicouches frittés par diffusion, nos éléments conservent leur géométrie de pore et leur seuil d'arrêt micrométrique sous de fortes fluctuations de débit.",
        "Fabriqués en acier inoxydable résistant AISI 304L et 316L ainsi qu'en alliages de nickel, ces filtres offrent une parfaite compatibilité avec les huiles synthétiques, les carburants et le fioul lourd (HFO). Leur assemblage soudé permet de multiples cycles de nettoyage par ultrasons et inversion de flux.",
      ],
      keyPoints: [
        "Stratifiés frittés par diffusion et toiles denses maintenant les pores sous de fortes pulsations différentielles",
        "Structure entièrement métallique supprimant la migration de fibres et protégeant servovalves et injecteurs",
        "Régénération complète par ultrasons et rétrolavage, réduisant durablement les déchets de cartouches consommables",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
        alt: "Détail d'un élément filtrant en toile inox pour carburant et huile",
      },
    },
    specTable: {
      heading: "Configurations de Médias Filtrants pour Huiles et Carburants",
      intro: "Médias métalliques types pour circuits hydrauliques haute pression, kérosène aviation, lubrification et fiouls lourds.",
      columns: [
        { key: "service", label: "Circuit / Application", align: "left" },
        { key: "media", label: "Type de Média Filtrant", align: "left" },
        { key: "rating", label: "Seuil Nominal", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "cleaning", label: "Mode de Nettoyage", align: "right" },
      ],
      rows: [
        {
          service: "Puissance Hydraulique Haute Pression",
          media: "Média fritté multicouche plissé",
          rating: "3 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Bain à ultrasons / rétrolavage",
        },
        {
          service: "Kérosène Aviation & Lignes Carburant",
          media: "Toile tissée hollandaise sergée",
          rating: "1 µm – 15 µm",
          alloys: "AISI 304L / 316L",
          cleaning: "Rinçage solvant / flux inverse",
        },
        {
          service: "Lubrification Turbines & Paliers",
          media: "Cartouche cylindrique en toile hollandaise",
          rating: "10 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Ultrasons / rinçage solvant",
        },
        {
          service: "Conditionnement de Fioul Lourd (HFO)",
          media: "Panier ou cylindre en toile soudée",
          rating: "50 µm – 250 µm",
          alloys: "AISI 316L / Inconel",
          cleaning: "Soufflage vapeur / bain chimique",
        },
      ],
      caption: "Toutes les finesses de coupure, dimensions de cartouches et raccords sont usinés sur mesure selon le cahier des charges.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos filtres métalliques pour carburants et fluides hydrauliques combinent tenue mécanique, précision micrométrique et excellente nettoyabilité.",
      items: [
        {
          title: "Haute Résistance à l'Écrasement",
          text: "Armatures de support robustes et tubes intérieurs perforés empêchant l'écrasement lors des démarrages à froid à forte viscosité.",
        },
        {
          title: "Zéro Relargage de Fibres",
          text: "Toiles tissées de précision et liaison par frittage éliminant tout risque de détachement particulaire vers l'aval du circuit.",
        },
        {
          title: "Large Compatibilité Chimique",
          text: "Inox AISI 304L, 316L et alliages de nickel résistant aux hydrocarbures acides, aux composés soufrés et aux huiles synthétiques.",
        },
        {
          title: "Nettoyabilité Répétable",
          text: "Assemblage entièrement soudé permettant des nettoyages répétés par ultrasons et rétrolavages sans altération mécanique.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez les solutions complémentaires de filtration d'hydrocarbures et les toiles métalliques conçues par MutualFilter.",
      cards: [
        {
          title: "Filtration des Huiles, Carburants et Pétrochimie",
          text: "Solutions de séparation métallique pour raffineries, séparation amont et circuits de puissance haute pression.",
          to: "/OilFuelFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Présentation générale de la filtration des huiles et carburants",
          },
        },
        {
          title: "Séparation Pétrole et Gaz",
          text: "Contrôle du sable en puits, dévésiculage de gaz et traitement des eaux de production en milieu hydrocarbures.",
          to: "/OilGasSeparationPage",
          badge: "Application Associée",
          linkLabel: "Explorer Pétrole et Gaz",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Panneau filtrant multicouche en inox pour séparation pétrole et gaz",
          },
        },
        {
          title: "Filtration chimique et de procédés",
          text: "Grilles de maintien de lit catalytique, protection des compresseurs et filtration d'hydrocarbures à haute température.",
          to: "/ChemicalFiltrationPage#petrochemical",
          badge: "Application Associée",
          linkLabel: "Explorer Chimie et Procédés",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
            alt: "Cuve de filtration industrielle haute pression en pétrochimie",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Éléments cylindriques et plissés entièrement soudés en acier inoxydable pour circuits carburant et hydraulique.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Cartouches filtrantes inox plissées et cylindriques pour carburants",
          },
        },
        {
          title: "Toile Métallique Frittée",
          text: "Stratifiés frittés multicouches assurant une géométrie de pores rigide et une rétention sans relargage.",
          to: "/SinteredWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer la Toile Frittée",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disque laminé fritté multicouche pour filtration hydraulique et huile",
          },
        },
      ],
    },
    cta: {
      id: "oil-fuel-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Éléments Filtrants Carburant et Huile",
      lead: "Indiquez votre seuil de filtration, débit opérationnel, type de fluide et encombrements pour une étude technique détaillée.",
      secondaryLabel: "Retour à la Filtration Carburant",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "OilFuelFiltrationApplications",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Filtração de Óleo, Combustível e Petroquímica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Telas e Elementos de Aço Inoxidável para Combustível e Fluidos Hidráulicos",
      lead: "Meios filtrantes metálicos de precisão, laminados sinterizados por difusão e elementos cilíndricos desenvolvidos para linhas de combustível de alta pressão, circuitos hidráulicos, lubrificação de turbinas e óleo combustível pesado sem desprendimento de fibras.",
      secondaryCta: {
        label: "Filtração de Óleo e Combustível",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
        alt: "Detalhe do elemento de malha em aço inoxidável para filtração de combustível e fluido hidráulico",
      },
      cardNote: "Construção totalmente metálica e zero migração de fibras downstream",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de Filtragem", value: "1 µm – 250 µm" },
        { label: "Tramas Padrão", value: "Plain / Twill Dutch / Sinterizado" },
        { label: "Ligas Padrão", value: "AISI 304L / 316L / Inconel" },
        { label: "Limpabilidade", value: "Ultrassom / Retrolavagem" },
      ],
    },
    overview: {
      eyebrow: "Transmissão Fluida e Condicionamento de Combustível",
      heading: "Retenção Confiável de Partículas para Hidrocarbonetos e Fluidos Hidráulicos em Alta Pressão",
      paragraphs: [
        "Circuitos de transmissão de potência hidráulica, linhas de distribuição de querosene de aviação e sistemas de lubrificação de turbinas operam sob intensas pulsações e pressões diferenciais contínuas. Meios descartáveis de polímero ou celulose trazem sérios riscos de canalização, colapso estrutural e desprendimento de fibras que danificam servoválvulas, mancais e bicos injetores.",
        "A MutualFilter fornece meios filtrantes totalmente metálicos desenvolvidos para eliminar essas vulnerabilidades. Com o uso de telas em trama holandesa lisa, telas Dutch sarja de alta densidade e laminados multicamadas sinterizados por difusão, nossos elementos preservam a estabilidade dos poros e o corte micrométrico mesmo diante de variações bruscas de vazão.",
        "Fabricados em aços inoxidáveis AISI 304L e 316L e ligas de níquel de alto desempenho, os elementos oferecem ampla compatibilidade com fluidos sintéticos, combustíveis e óleo pesado (HFO). A estrutura soldada possibilita sucessivas etapas de limpeza por retrolavagem e banhos de ultrassom.",
      ],
      keyPoints: [
        "Laminados sinterizados por difusão e telas Dutch densas que mantêm os poros intactos sob severos pulsos de pressão",
        "Arquitetura totalmente soldada que elimina a migração de fibras e protege servoválvulas e bicos injetores",
        "Totalmente regenerável por ultrassom e retrolavagem contínua, reduzindo o descarte de elementos consumíveis",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
        alt: "Detalhe do elemento de malha em aço inoxidável para filtração de óleo e combustível",
      },
    },
    specTable: {
      heading: "Configurações de Meios Filtrantes para Óleo e Combustível",
      intro: "Meios metálicos comuns para linhas de alta pressão hidráulica, querosene de aviação, lubrificação de turbinas e óleos combustíveis pesados.",
      columns: [
        { key: "service", label: "Circuito / Aplicação", align: "left" },
        { key: "media", label: "Estrutura do Meio Filtrante", align: "left" },
        { key: "rating", label: "Grau Nominal", align: "right" },
        { key: "alloys", label: "Ligas Típicas", align: "right" },
        { key: "cleaning", label: "Método de Limpeza", align: "right" },
      ],
      rows: [
        {
          service: "Potência Hidráulica de Alta Pressão",
          media: "Malha sinterizada multicamada plissada",
          rating: "3 µm – 25 µm",
          alloys: "AISI 316L",
          cleaning: "Banho de ultrassom / retrolavagem",
        },
        {
          service: "Querosene de Aviação e Combustível",
          media: "Tela metálica de trama holandesa sarja",
          rating: "1 µm – 15 µm",
          alloys: "AISI 304L / 316L",
          cleaning: "Enxágue com solvente / retrolavagem",
        },
        {
          service: "Lubrificação de Turbinas e Mancais",
          media: "Cartucho cilíndrico em trama holandesa lisa",
          rating: "10 µm – 50 µm",
          alloys: "AISI 304 / 316L",
          cleaning: "Ultrassom / circulação de solvente",
        },
        {
          service: "Condicionamento de Óleo Pesado (HFO)",
          media: "Cesto ou cilindro de tela soldada",
          rating: "50 µm – 250 µm",
          alloys: "AISI 316L / Inconel",
          cleaning: "Purga a vapor / banho químico",
        },
      ],
      caption: "Todas as faixas de retenção, dimensões de cartuchos e conexões são fabricadas sob medida conforme o projeto hidráulico.",
    },
    features: {
      heading: "Destaques Operacionais e Construtivos",
      intro: "Nossas soluções totalmente metálicas oferecem resistência mecânica ao colapso, retenção micrométrica estável e excelente limpabilidade.",
      items: [
        {
          title: "Alta Resistência ao Colapso",
          text: "Telas de suporte robustas e núcleos perfurados internos suportam picos de pressão e partidas a frio com fluidos de alta viscosidade.",
        },
        {
          title: "Zero Desprendimento de Fibras",
          text: "Telas de alta densidade e sinterização por difusão garantem que os fluidos downstream fiquem totalmente livres de fibras e impurezas.",
        },
        {
          title: "Ampla Compatibilidade Química",
          text: "Ligas AISI 304L, 316L e níquel suportam compostos sulfurosos, hidrocarbonetos ácidos e fluidos sintéticos agressivos.",
        },
        {
          title: "Limpabilidade Repetível",
          text: "Construção soldada durável que tolera ciclos contínuos de ultrassom e retrolavagem sem alteração mecânica da malha.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Produtos Relacionados",
      intro: "Conheça soluções integradas de filtração de hidrocarbonetos e telas de arame de precisão fabricadas pela MutualFilter.",
      cards: [
        {
          title: "Filtração de Óleo, Combustível e Petroquímica",
          text: "Soluções completas de separação metálica para refinarias, separação primária e sistemas hidráulicos de alta pressão.",
          to: "/OilFuelFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Visão geral de filtração de óleo e combustível",
          },
        },
        {
          title: "Separação de Óleo e Gás",
          text: "Controle de areia em poços, desembaçamento em depuradores e purificação de água produzida em hidrocarbonetos.",
          to: "/OilGasSeparationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Óleo e Gás",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Painel filtrante multicamada de aço inoxidável para separação de óleo e gás",
          },
        },
        {
          title: "Filtração química e de processos",
          text: "Proteção de leitos catalíticos de craqueamento, compressores de reforma e retenção de particulados em altas temperaturas.",
          to: "/ChemicalFiltrationPage#petrochemical",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Química e Processos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
            alt: "Vaso de filtração industrial de alta pressão em unidade petroquímica",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cartuchos cilíndricos e plissados totalmente soldados em aço inoxidável para linhas de combustível e hidráulica.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Cartuchos filtrantes em aço inoxidável para óleo e combustível",
          },
        },
        {
          title: "Malha de Arame Sinterizada",
          text: "Laminados sinterizados por difusão que oferecem rigidez de poros e retenção de partículas sem desprendimento de meio.",
          to: "/SinteredWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Malha Sinterizada",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado sinterizado multicamadas para filtração de óleo e hidráulica",
          },
        },
      ],
    },
    cta: {
      id: "oil-fuel-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite uma Cotação para Filtros de Combustível e Óleo",
      lead: "Informe a retenção micrométrica desejada, vazão operacional, tipo de fluido e dimensões para análise e proposta técnica.",
      secondaryLabel: "Voltar para Filtração de Combustível",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
